import React, { Fragment } from 'react';

import defer from 'lodash/defer';

import {
  LINK_TYPE_INTERNAL,
  LINK_TYPE_EXTERNAL
} from 'zoomforth_codesbane_viewer/const/link_types.js';

import {
  prependSchemeIfMissing
} from 'zoomforth_zfutils/url_validators.js';

import { slugifyString } from 'zoomforth_zfutils/url_utils.js';

import {
  getMenuItemById,
  getSubpageById,
  getCurrentSubpage
} from 'zoomforth_codesbane_viewer/reducers/index.js'; 

import {
  setCurrentSubpageId,
  scrollToTop,
  goToContent,
  setOverlayContentUuid,
  scrollToSection
} from 'zoomforth_codesbane_viewer/action_creators/viewer.js';

import {
  getTemporaryMenuData,
  getIsHeaderEditorOpen
} from 'zoomforth_codesbane_editor/reducers/index.js';

import {
  editTargetForInternalLink
} from './internal_link_target.js';

import { 
  setSubpageData
} from './subpage.js';

import { 
  showModal
} from './editor_modals.js';

import { 
  generateMenuItemId
} from 'zoomforth_codesbane_editor/lib/id_generators.js';

import {
  ensureThrottledPageConfigSaving
} from './config.js';

import {
  generateSubpageId,
  makeNewSubpageData
} from 'zoomforth_codesbane_viewer/lib/subpages.js';

import {
  makeNewMenuItemData
} from 'zoomforth_codesbane_viewer/lib/navigation.js';

import {
  closeAllEditingFeatures 
} from 'zoomforth_codesbane_editor/action_creators/misc.js';


import { getIdFrom } from 'zoomforth_codesbane_viewer/lib/id_utils.js';
import { get as getPageHistoryTracker } from 'zoomforth_zf/appglobals/page-history-tracker.js';
import { info as showNotificationInfo } from 'zoomforth_app_basics/action_creators/notifications.js';

import { ExternalLinkIcon } from 'zoomforth_component_lib/app_modern_icons.jsx';

import ConfirmDeleteMenuItemModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_delete_menuitem_container.jsx';


export const editExistingMenuItem = (menuItemOrId)=>
  (dispatch, getState)=> {
    const menuItem = getMenuItemById(getState(), getIdFrom(menuItemOrId));

    dispatch(scrollToTop());
    dispatch(setTemporaryMenuData({
      menuItem,
      subpage: menuItem.isPrimaryForSubpage ?
          getSubpageById(getState(), menuItem.appAction.subpageId)
        :
          null
    }));
  };


export const editNewSubpageMenuItem = ()=>
  (dispatch)=> {

    const subpage = makeNewSubpageData({ id: generateSubpageId() });

    dispatch(setTemporaryMenuData({

      menuItem: makeNewMenuItemData({
        menuItemType: LINK_TYPE_INTERNAL, 
        isPrimaryForSubpage: true, 
        parentSubpageId: subpage.id
      }),
      subpage

    }));
  };


export const editNewSubmenuInternalLinkMenuItem = (parentSubpageId)=>
  (dispatch)=> {

    dispatch(setTemporaryMenuData({

      menuItem: makeNewMenuItemData({
        menuItemType: LINK_TYPE_INTERNAL, 
        isPrimaryForSubpage: false, 
        parentSubpageId
      }),
      subpage: null // Cannot edit subpage from submenu editor

    }));
  };


export const editNewSubmenuExternalLinkMenuItem = (parentSubpageId)=>
  (dispatch)=> {

    dispatch(setTemporaryMenuData({

      menuItem: makeNewMenuItemData({
        menuItemType: LINK_TYPE_EXTERNAL, 
        isPrimaryForSubpage: false, 
        parentSubpageId
      }),
      subpage: null // Cannot edit subpage from submenu editor

    }));
  };


export const editNewInternalLinkMenuItem = ()=>
  (dispatch)=> {

    dispatch(setTemporaryMenuData({

      menuItem: makeNewMenuItemData({
        menuItemType: LINK_TYPE_INTERNAL, 
        isPrimaryForSubpage: false, 
        parentSubpageId: null // start with null 
      }),
      subpage: null // Cannot change subpage from submenu editor

    }));
  };

export const editNewExternalLinkMenuItem = ()=>
  (dispatch)=> {

    dispatch(setTemporaryMenuData({

      menuItem: makeNewMenuItemData({
        menuItemType: LINK_TYPE_EXTERNAL
      }),
      subpage: null // Cannot change subpage from external link editor

    }));
  };


export const editTemporaryMenuItemTargetForInternalLink = (sectionOrId)=>
  (dispatch, getState)=> {
    const temporaryMenuData = getTemporaryMenuData(getState());
    dispatch(editTargetForInternalLink(
      sectionOrId, 
      temporaryMenuData.menuItem.appAction.sectionId, 
      temporaryMenuData.menuItem.appAction.contentUuid
    ));
  };


export const closeMenuItemEditor = ()=>
  (dispatch)=> {
    dispatch(setTemporaryMenuData(null));
  };


export const saveTemporaryMenuData = ()=>
  (dispatch, getState)=> {
    const temporaryMenuData = getTemporaryMenuData(getState());

    if (!temporaryMenuData) {
      throw new Error('No temporary menu data to save');
    }

    const temporaryMenuItem = temporaryMenuData.menuItem;
    const temporarySubpage = temporaryMenuData.subpage;
    const isNewMenuItem = !temporaryMenuItem.id;
    const isNewSubpage = temporarySubpage && !getSubpageById(getState(), temporarySubpage.id);

    if (temporarySubpage) {
      const slug = slugifyString(temporarySubpage.slug);
      temporarySubpage.slug = slug;
      temporaryMenuItem.appAction.subpageSlug = slug;
      dispatch(setSubpageData(temporarySubpage.id, temporarySubpage));
    }

    if (!isNewMenuItem) {
      dispatch(setMenuItem(temporaryMenuItem));

    } else {

      if (isNewSubpage) {
        getPageHistoryTracker().trackAddSubpage(temporaryMenuItem.title);
        dispatch(setCurrentSubpageId(temporarySubpage.id));
      
      } else if (temporaryMenuItem.isSubmenuItem) {
        getPageHistoryTracker().trackAddMenuAnchorLink(
          temporaryMenuItem.appAction.contentUuid,
          temporaryMenuItem.title
        );
      }

      dispatch(createMenuItem(temporaryMenuItem));

    }

    dispatch(ensureThrottledPageConfigSaving());
    dispatch(closeMenuItemEditor());
  };


export const deleteMenuItem = (menuItemOrId)=>
  (dispatch, getState)=> {
    const menuItem =  getMenuItemById(getState(), getIdFrom(menuItemOrId));
    if (menuItem.isPrimaryForSubpage) {
      throw new Error(`Cannot delete menuItemId=${menuItem.id}, because it is a primary link to a subpage. You must dispatch delete subpage.`);
    }
    dispatch({
      type: 'DELETE_MENU_ITEMS',
      menuItemIds: [menuItem.id]
    });
    dispatch(ensureThrottledPageConfigSaving());
  };


export const confirmDeleteMenuItem = (menuItemOrId)=>
  (dispatch, getState)=> {
    const menuItem =  getMenuItemById(getState(), getIdFrom(menuItemOrId));

    dispatch(showModal(ConfirmDeleteMenuItemModal, { menuItem }));
  };


export const setTemporaryMenuData = (menuData)=>
  (dispatch)=> {

    let menuItem = null;
    let subpage = null;
    if (menuData) {
      menuItem = menuData.menuItem;
      subpage = menuData.subpage;
    }

    dispatch({
      type: 'SET_TEMPORARY_MENU_DATA',
      menuItem,
      subpage
    });
  };


export const createMenuItem = (menuItem)=>
  (dispatch, getState)=> {
    const menuItemId = menuItem.id ? menuItem.id : generateMenuItemId();

    // check if we already have a menu item with this ID
    if (getMenuItemById(getState(), menuItemId)) {
      throw Error('Menu item ID already exists!');
    }

    if (menuItem.appAction.type === LINK_TYPE_EXTERNAL) {
      menuItem = {
        ...menuItem,
        appAction: {
          ...menuItem.appAction,
          url: prependSchemeIfMissing(menuItem.appAction.url)
        }
      };
    }
    
    dispatch({
      type: 'CREATE_MENU_ITEM',
      menuItem,
      id: menuItemId
    });
  };


export const setMenuItem = (menuItem)=>
  (dispatch)=> {

    if (menuItem.appAction.type === LINK_TYPE_EXTERNAL) {
      menuItem = {
        ...menuItem,
        appAction: {
          ...menuItem.appAction,
          url: prependSchemeIfMissing(menuItem.appAction.url)
        }
      };
    }

    dispatch({
      type: 'SET_MENU_ITEM',
      menuItem
    });

    dispatch(ensureThrottledPageConfigSaving());
  };


export const setMenuItemsOrder = (menuItems)=>
  (dispatch)=> {
    dispatch({
      type: 'SET_MENU_ITEMS_ORDER',
      menuItems
    });

    dispatch(ensureThrottledPageConfigSaving());
  };


const doAppAction = (appAction)=>
  (dispatch)=> {
    if (appAction.contentUuid) {
      dispatch(goToContent(appAction.contentUuid));

      if (appAction.shouldOpenOverlay) {
        dispatch(setOverlayContentUuid(appAction.contentUuid));
      }

    } else if (appAction.sectionId) {
      dispatch(scrollToSection(appAction.sectionId));
    }    
  };



export const interactWithMenuItem = (menuItem, { shouldCloseEditingFeatures = true }={})=>
  (dispatch, getState)=> {
    const currentSubpage = getCurrentSubpage(getState());

    if (getIsHeaderEditorOpen(getState())) {
      return;
    }

    if (menuItem.appAction.type === LINK_TYPE_INTERNAL) {
      
      if (
        menuItem.appAction.subpageId === currentSubpage.id && 
        !(menuItem.appAction.contentUuid || menuItem.appAction.sectionId)
      ) {
        dispatch(scrollToTop());
      
      } else {
        dispatch(setCurrentSubpageId(menuItem.appAction.subpageId)); 
      }

      if (shouldCloseEditingFeatures) {
        dispatch(closeAllEditingFeatures());
      }

      defer(()=> {
        dispatch(doAppAction(menuItem.appAction)); // Let the subpage switch do its thing
      });

    } else if (menuItem.appAction.type === LINK_TYPE_EXTERNAL) {

      if (menuItem.appAction.target !== '_blank') {
        dispatch(showNotificationInfo({
          children: <Fragment>
            <span></span>
            <p><ExternalLinkIcon /> You clicked an external link. </p>
            <p>In the published site, 
            this link will open the following URL in the same browser tab. 
            Preview the site to see the full experience.</p>
            <p className="notification-link"><a href={menuItem.appAction.url} target="_blank">
            {menuItem.appAction.url}</a></p>
          </Fragment>
        }));
      
      } else {
        window.open(menuItem.appAction.url, '_blank');
      }
    }
  };


export const interactWithSubmenuItem = (submenuItem)=>
  (dispatch, getState)=> {
    if (getIsHeaderEditorOpen(getState())) {
      return;
    }

    dispatch(closeAllEditingFeatures());

    defer(()=> {
      // In the editor, submenu items are only ever clicked from the same subpage
      dispatch(doAppAction(submenuItem.appAction));
    });
  };
